
import { defineStore } from 'pinia'
import axios from "axios";
import {ApiClient} from "@/services/ApiService";
import {useAcademiesStore} from "@/stores/academies";
import AcademyService from "@/services/academy/AcademyService";
import router from "../router";

export const useAuthUserStore = defineStore('auth', {
    state: () => ({
        token: {
            token_type: null,
            expires_in: null,
            access_token: null,
            refresh_token: null
        },
        selected_academy_uuid: null,
        academySelectionRequired: false,
        user: {
            academy: {
                branding: {
                    logo_url: null
                },
                uuid: null,
                saas_subscriptions: [{
                    status: '',
                    saas_plan: {
                        key: ''
                    }
                }]
            },
            avatar: "/images/avatar-bp.jpg",
            firstname: null,
            intercom_hash: null
        },
        isNewUser: true
    }),
    getters: {
        isLoggedIn:(state) =>  !!   state.token.accessToken,
        getApiClient: (state) => new ApiClient(state.token.accessToken),
        isStaff: (state) => state.user?.is_staff === true,
        //getSelectedAcademyUuid: (state) => state.selected_academy_uuid
    },
    actions: {

        signup (credentials) {
            return axios
                .post(process.env.VUE_APP_API_URL + '/register', credentials)
                .then(({ data }) => {
                    console.log("Los datos recibidos son:", data)
                    this.setTokenData(data)
                })
                .then( () => {
                    this.getUser()
                })
        },
        login (credentials) {
            return axios
                .post(process.env.VUE_APP_API_URL + '/login', credentials)
                .then(({ data }) => {
                    console.log("Los datos recibidos son:", data)
                    this.setTokenData(data)
                })
                .then( () => {
                    return this.getUser()
                })
        },
        /*
         * Revoca el token en el servidor (POST /logout) antes de borrar la sesión local;
         * si no, seguiría valiendo hasta el siguiente login. Tras un 401 no hay nada que
         * revocar ({revoke: false}). Un fallo de red no impide cerrar la sesión.
         */
        logout ({ revoke = true } = {}) {
            const revocation = revoke && localStorage.getItem('authData')
                ? axios.post(process.env.VUE_APP_API_URL + '/logout').catch(() => {})
                : Promise.resolve()

            return revocation.then(() => {
                localStorage.removeItem('authData')
                localStorage.removeItem('selectedAcademyUuid')
                delete axios.defaults.headers.common['Authorization']
                location.reload()
            })
        },
        isNewUser (isNewUser) {
            this.isNewUser = isNewUser
        },
        selectAcademy(academyUuid) {
            console.log("Cambiando a academia:", academyUuid)
            localStorage.setItem('selectedAcademyUuid', academyUuid)
            location.reload()
        },
        getUserV2 () {
            return axios
                .get(process.env.VUE_APP_API_URL + '/me')
        },
        getUser () {
            return axios
                .get(process.env.VUE_APP_API_URL + '/me')
                .then( ({data}) => {
                    console.log("Los datos recibidos para el usuario son:", data)
                    if (!data.is_owner)
                    {
                        this.logout()
                        return
                    }

                    this.user = data

                    if (window.Intercom)
                    {
                        console.log("intercom is loaded");
                        window.Intercom("boot", {
                            app_id: "ciiw9mpa",
                            "Company name": data.academy.title,
                            "Company ID": data.academy.uuid,
                            "Company website": 'https://' + data.academy.subdomain + '.mybrainspro.com',
                            user_id: data.uuid,
                            user_hash: data.intercom_hash,
                            name: data.firstname + ' ' + data.lastname,
                            email: data.email,
                            plan_type: data.academy.saas_subscriptions[0].saas_plan.title,
                            monthly_spend: data.academy.saas_subscriptions[0].total,
                            subscription_status: data.academy.saas_subscriptions[0].status
                        });
                    }

                    /*
                     * ---------------------
                     * Comprobará el estado de 'Saas Subscription'
                     * enviará a 'current-subscription'
                     * en caso de que el estado sea past_due.
                     * ---------------------
                     */
                    console.log("El estado de la suscriptión es: ", this.user.academy.saas_subscriptions[0].status)
                    if(this.user.academy.saas_subscriptions[0].status == 'past_due' || this.user.academy.saas_subscriptions[0].status == 'canceled')
                    {
                        router.push('/myaccount/current-subscription')
                    }

                    return this.resolveSelectedAcademy()
                })
                .catch( (error) => {
                    console.log("Se ha producido un error", error)
                })
        },
        /*
         * Decide qué academia debe quedar seleccionada tras el login.
         * Si el usuario tiene una sola academia disponible, se selecciona
         * automáticamente (pisando cualquier valor residual en localStorage
         * de una sesión/usuario anterior). Si tiene varias (plan Corporate),
         * solo se conserva la seleccionada previamente si sigue perteneciendo
         * al usuario; si no, se marca que hace falta elegir explícitamente.
         */
        resolveSelectedAcademy () {
            const academy = this.user.academy
            const isCorporatePlan = academy.saas_subscriptions[0]?.saas_plan?.key === 'plan_corporate'

            if (!isCorporatePlan)
            {
                this.academySelectionRequired = false
                localStorage.setItem('selectedAcademyUuid', academy.uuid)
                return useAcademiesStore().getAcademy()
            }

            return AcademyService.index()
                .then(({ data }) => {
                    const academies = data ?? []

                    if (academies.length <= 1)
                    {
                        this.academySelectionRequired = false
                        localStorage.setItem('selectedAcademyUuid', academies[0]?.uuid ?? academy.uuid)
                        return useAcademiesStore().getAcademy()
                    }

                    const cachedUuid = localStorage.getItem('selectedAcademyUuid')
                    const cachedIsValid = academies.some((a) => a.uuid === cachedUuid)

                    if (cachedIsValid)
                    {
                        this.academySelectionRequired = false
                        return useAcademiesStore().getAcademy()
                    }

                    localStorage.removeItem('selectedAcademyUuid')
                    this.academySelectionRequired = true
                })
                .catch( (error) => {
                    console.log("Se ha producido un error obteniendo las academias", error)
                    this.academySelectionRequired = false
                    localStorage.setItem('selectedAcademyUuid', academy.uuid)
                    return useAcademiesStore().getAcademy()
                })
        },
        rememberPassword (request) {
            return axios
                .post(process.env.VUE_APP_API_URL + '/remember', request)
                .then(({data}) => {
                    console.log("Result: ", data)
                });
        },
        resetPassword (request) {
            return axios
                .post(process.env.VUE_APP_API_URL + '/reset', request)
                .then(({data}) => {
                    console.log("Result: ", data)
                });
        },
        //-----------
        // Mutations!
        //-----------
        setTokenData (authData) {
            localStorage.setItem('authData', JSON.stringify(authData))
            axios.defaults.headers.common['Authorization'] = `Bearer ${
                authData.access_token
            }`
            this.token.accessToken = authData.access_token
        }
    }
})